import "./notification_popover.less";
import React, { useMemo } from "react";

import NotificationIcon from "@src/assets/icons/notification.svg";
import { DATA_CONFIRMATION_DATE } from "@src/constants";
import { DataConfirmationNotificationItem } from "@src/features/notification/components/notification_confirmation_item/index";
import useFeatureToggle from "@src/hooks/use_feature_toggle";
import { useGetNotificationsQuery } from "@src/services/apis/notificationsApi";
import { DataConfirmationNotification } from "@src/types/notification_types";
import { Popover, Collapse, Badge, CollapseProps } from "antd";
import dayjs from "dayjs";

const NotificationPopover: React.FC = () => {
  const plResourcePlanAuditEnabled: boolean = useFeatureToggle(
    "confirmationOutOfRange"
  );
  // this is only for testing it outside the cut-off range. When the feature is deployed and toggle removed
  // this can be than add to backend so that it will have always by default the data range
  const startDate = plResourcePlanAuditEnabled
    ? dayjs().startOf("month").format("YYYY-MM-DD")
    : dayjs()
        .set("date", DATA_CONFIRMATION_DATE.CUT_OFF_DAY)
        .format("YYYY-MM-DD");

  // this is only for testing it outside the cut-off range. When the feature is deployed and toggle removed
  // this can be than add to backend so that it will have always by default the data range
  const endDate = plResourcePlanAuditEnabled
    ? dayjs().endOf("month").format("YYYY-MM-DD")
    : dayjs()
        .set("date", DATA_CONFIRMATION_DATE.LAST_CONFIRMATION_DAY)
        .format("YYYY-MM-DD");

  const { data: notifications } = useGetNotificationsQuery({
    startDate: startDate,
    endDate: endDate,
  });

  const dataConfirmationNotifications: DataConfirmationNotification[] =
    useMemo(() => {
      if (!notifications || notifications.totalNotifications === 0) return null;

      return notifications.dataConfirmationNotifications;
    }, [notifications]);

  const notificationCollapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: `Confirmation needed (${dataConfirmationNotifications?.length})`,
      children: (
        <>
          {dataConfirmationNotifications?.map(
            (dataConfirmationNotification) => (
              <DataConfirmationNotificationItem
                key={dataConfirmationNotification.projectId}
                dataConfirmation={dataConfirmationNotification}
                visibleUntil={dataConfirmationNotification?.visibleUntil}
              />
            )
          )}
        </>
      ),
    },
  ];

  function getPopoverContent(): React.ReactElement {
    return (
      <div className="notification-popover__item">
        <Collapse
          items={notificationCollapseItems}
          defaultActiveKey={["1"]}
          accordion
        />
      </div>
    );
  }

  return (
    <div className="notification-popover" data-testid="notification-popover">
      {notifications?.totalNotifications > 0 && (
        <Popover
          content={getPopoverContent()}
          trigger="click"
          classNames={{ root: "notification-popover__popover" }}
        >
          <Badge
            count={notifications?.totalNotifications}
            className="notification-popover__batch"
          >
            <NotificationIcon
              role="notification"
              data-testid="notification-icon"
            />
          </Badge>
        </Popover>
      )}
    </div>
  );
};

export default NotificationPopover;
